import {
  Button,
  Flex,
  IconButton,
  Paper,
  Select,
  Text,
  Textarea,
} from "@pillar-ui/core";
import { Close, Star } from "@pillar-ui/icons";
import { createPortal } from "react-dom";
import {
  DIALOG_MAX_HEIGHT,
  DIALOG_MAX_WIDTH,
  DIALOG_MIN_HEIGHT,
} from "../styles/variables";
import { useEffect, useState } from "react";
import { addComment } from "../services";
import { useAppStateContext } from "../common/contexts";
import colors from "../styles/colors";
import RatingStars from "./rating-stars";
import orderBy from "lodash/orderBy";

const CommentEditor = (props) => {
  const { open, onClose, commentEditorData, commentList } = props;
  const [rating, setRating] = useState(0);
  const [commentText, setCommentText] = useState("");
  const [loading, setLoading] = useState(false);
  const [filterByLevel, setFilterByLevel] = useState(-1);
  const { handleOpenToast } = useAppStateContext();

  const getCreateTimeDisplay = (createTime) => {
    const dateObj = new Date(createTime);
    const todayObj = new Date();
    const isToday =
      dateObj.getDate() === todayObj.getDate() &&
      dateObj.getMonth() === todayObj.getMonth() &&
      dateObj.getFullYear() === todayObj.getFullYear();

    if (isToday) {
      return `${String(dateObj.getHours()).padStart(2, "0")}:${String(
        dateObj.getMinutes()
      ).padStart(2, "0")}`;
    }
    return `${dateObj.getDate()}/${
      dateObj.getMonth() + 1
    }/${dateObj.getFullYear()} ${String(dateObj.getHours()).padStart(
      2,
      "0"
    )}:${String(dateObj.getMinutes()).padStart(2, "0")}`;
  };

  const handleChangeRating = (level) => {
    setRating(level);
  };

  const resetForm = () => {
    setRating(0);
    setCommentText("");
    setLoading(false);
    setFilterByLevel(-1);
  };

  const handleSubmit = async (evt) => {
    evt?.preventDefault?.();
    try {
      if (!rating) {
        return;
      }
      setLoading(true);
      const newComment = {
        id: commentEditorData?.id,
        text: commentText,
        rating,
      };
      await addComment(newComment, (err) => {
        setLoading(false);
        handleOpenToast?.("danger", err?.message);
      });
      resetForm();
      onClose?.();
      handleOpenToast?.("success", "Đánh giá thành công!");
    } catch (err) {
      setLoading(false);
      handleOpenToast?.("danger", err?.message);
    }
  };

  useEffect(() => {
    if (open) {
      setCommentText(commentEditorData?.text || "");
      setRating(commentEditorData?.rating || 0);
    } else {
      resetForm();
    }
  }, [open, commentEditorData]);

  if (!open) return null;
  return createPortal(
    <Paper
      className="fixed inset-0 flex items-center justify-center"
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 999,
      }}
    >
      <form onSubmit={handleSubmit}>
        <Paper
          onClick={(e) => e.stopPropagation()}
          corner="4"
          width="calc(100vw - 60px)"
          height="70vh"
          background="Se2"
          p="4"
          style={{
            maxHeight: DIALOG_MAX_HEIGHT,
            minHeight: DIALOG_MIN_HEIGHT,
            maxWidth: DIALOG_MAX_WIDTH,
          }}
        >
          <Flex direction="col" style={{ height: "100%" }}>
            <Paper style={{ marginBottom: 0 }}>
              <Flex justify="between">
                <Text size="7" color="i" style={{ fontWeight: 700 }}>
                  {`Đánh Giá ${commentEditorData?.name}`}
                </Text>
                <Paper onClick={onClose}>
                  <IconButton variant="text" icon={<Close />} color="p" />
                </Paper>
              </Flex>
            </Paper>
            <Flex style={{ paddingBottom: 8 }}>
              <Select
                variant="outline"
                value={filterByLevel}
                onChange={(evt) => setFilterByLevel(evt?.target?.value)}
              >
                <option value={-1}>Tất cả</option>
                <option value={5}>5 *</option>
                <option value={4}>4 *</option>
                <option value={3}>3 *</option>
                <option value={2}>2 *</option>
                <option value={1}>1 *</option>
              </Select>
            </Flex>
            <Flex
              direction="col"
              gap="5"
              style={{ flex: 1, paddingBottom: 20, overflow: "hidden" }}
            >
              <Paper
                background="b10"
                corner="4"
                p="3"
                style={{
                  flex: 1,
                  borderWidth: 1,
                  borderColor: colors.black,
                  overflow: "hidden auto",
                  backgroundColor: colors.white,
                  scrollbarWidth: "none", // Firefox
                  msOverflowStyle: "none", // IE, Edge
                }}
              >
                {orderBy(commentList, ["createTime"], ["desc"])
                  ?.filter?.((cmt) => {
                    return (
                      Number(filterByLevel) === -1 ||
                      cmt?.rating === Number(filterByLevel)
                    );
                  })
                  ?.map?.((cmt, index) => {
                    return (
                      <Paper
                        key={`comment_item_${index + 1}`}
                        as={Flex}
                        direction="col"
                        gap="1"
                        style={{
                          borderBottomWidth: 1,
                          borderBottomStyle: "solid",
                          borderBottomColor: colors.lightYellow,
                          paddingBottom: 16,
                          paddingTop: 16,
                        }}
                      >
                        <Flex items="center" gap="4">
                          <RatingStars value={cmt?.rating} size="sm" />
                          <Text size="2" color="b" style={{ fontWeight: 400 }}>
                            {getCreateTimeDisplay(cmt?.createTime)}
                          </Text>
                        </Flex>
                        <Text size="2" color="b" style={{ fontWeight: 400 }}>
                          {cmt?.text}
                        </Text>
                      </Paper>
                    );
                  })}
              </Paper>
              <RatingStars value={rating} onClickItem={handleChangeRating} />
              <Textarea
                variant="bordered"
                placeholder="Bình luận..."
                color="i"
                required
                value={commentText}
                onChange={(evt) => setCommentText(evt?.target?.value)}
                style={{ height: 100 }}
              />
            </Flex>
            <Flex items="end" justify="end" gap="3" style={{ marginBottom: 8 }}>
              <Button
                variant="solid"
                corner="4"
                color="p"
                style={{ width: 140, height: 40, fontSize: 18 }}
                type="submit"
                state={loading ? "loading" : "idle"}
                loadingText="Đang lưu..."
              >
                Đánh giá
              </Button>
              <Button
                variant="outline"
                corner="4"
                color="p"
                style={{ width: 140, height: 40, fontSize: 18 }}
                onClick={onClose}
              >
                Thoát
              </Button>
            </Flex>
          </Flex>
        </Paper>
      </form>
    </Paper>,
    document.body
  );
};

export default CommentEditor;
